/**
 * How often a hand wins, by dealing out the rest of the deck.
 *
 * Monte Carlo rather than exhaustive: on the flop with five opponents the exact
 * count runs into billions of deals, and a few hundred samples put the answer
 * inside a percentage point — which is far finer than any decision made from it.
 *
 * Pure, like everything else in this package. Randomness arrives as an argument,
 * so the same seed gives the same answer and a test can pin one down exactly.
 * That matters more than it sounds: an opponent that plays differently every
 * time it is asked the same question cannot be debugged.
 *
 * The opponents are dealt random hands. That is a real assumption and worth
 * saying out loud — actual opponents do not call with random hands, so this
 * reads high against players who only play good ones. The caller compensates by
 * asking for more than the raw price; it is not this function's job to guess at
 * anybody's range.
 */

import { orderedDeck, type Card } from './cards.ts';
import { evaluate } from './evaluator.ts';

export interface Equity {
  /**
   * Share of the pot this hand expects.
   *
   * This is the number a decision is made from: a chop between two players
   * contributes a half, not a whole. `win` and `tie` below are frequencies —
   * how often each outcome happened — which is what a human wants to read and
   * not what a pot-odds calculation wants to use.
   */
  readonly share: number;
  /** How often the hand won outright. */
  readonly win: number;
  /** How often it was tied, whatever the split was worth. */
  readonly tie: number;
  readonly samples: number;
}

/**
 * Estimate the share of the pot `hole` takes against `opponents` random hands.
 *
 * `board` may be empty (pre-flop), three, four or five cards. With five and one
 * opponent the answer is still sampled rather than solved, because the caller
 * should not have to care which case it is in — and sampling a decided board
 * converges immediately anyway.
 */
export function equity(
  hole: readonly Card[],
  board: readonly Card[],
  opponents: number,
  samples: number,
  random: () => number,
): Equity {
  if (hole.length !== 2) throw new Error('a hold-em hand is two cards');
  if (board.length > 5) throw new Error('a board is at most five cards');
  if (opponents < 1) throw new Error('there has to be somebody to beat');

  const seen = new Set<Card>([...hole, ...board]);
  if (seen.size !== hole.length + board.length) throw new Error('a card appears twice');

  const unseen = orderedDeck().filter((card) => !seen.has(card));
  const boardNeeded = 5 - board.length;
  const drawNeeded = boardNeeded + opponents * 2;
  if (drawNeeded > unseen.length) throw new Error('not enough cards left to deal');

  // One scratch array, shuffled just far enough each time. Allocating inside the
  // loop is what makes a naive version of this too slow to call per decision.
  const deck = unseen.slice();
  const full = new Array<Card>(7);
  const runout = new Array<Card>(5);
  for (let i = 0; i < board.length; i += 1) runout[i] = board[i] as Card;

  let win = 0;
  let tie = 0;
  let pot = 0;

  for (let sample = 0; sample < samples; sample += 1) {
    // Partial Fisher-Yates: only the cards actually needed are placed, and the
    // deck is left shuffled for the next sample rather than reset.
    for (let i = 0; i < drawNeeded; i += 1) {
      const j = i + Math.floor(random() * (deck.length - i));
      const picked = deck[j] as Card;
      deck[j] = deck[i] as Card;
      deck[i] = picked;
    }

    let next = 0;
    for (let i = 0; i < boardNeeded; i += 1) runout[board.length + i] = deck[next++] as Card;

    full[0] = hole[0] as Card;
    full[1] = hole[1] as Card;
    for (let i = 0; i < 5; i += 1) full[2 + i] = runout[i] as Card;
    const mine = evaluate(full, 7);

    let better = 0;
    let equal = 0;
    for (let o = 0; o < opponents; o += 1) {
      full[0] = deck[next++] as Card;
      full[1] = deck[next++] as Card;
      const theirs = evaluate(full, 7);
      if (theirs > mine) {
        better += 1;
        break; // one better hand is enough; the rest cannot change a loss
      }
      if (theirs === mine) equal += 1;
    }

    if (better > 0) continue;
    if (equal > 0) {
      tie += 1;
      pot += 1 / (equal + 1);
    } else {
      win += 1;
      pot += 1;
    }
  }

  return {
    share: pot / samples,
    win: win / samples,
    tie: tie / samples,
    samples,
  };
}

/**
 * A deterministic random source.
 *
 * xorshift128+, seeded from whatever the caller has to hand. Not for anything
 * that must be unpredictable — the shuffle has its own, built on HMAC — but for
 * an opponent's decisions, reproducibility is the property that matters and
 * unpredictability is not.
 */
export function seededRandom(seed: string): () => number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  let a = h || 1;
  let b = (h ^ 0x9e3779b9) >>> 0 || 2;
  return () => {
    a ^= a << 13;
    a >>>= 0;
    a ^= a >>> 17;
    a ^= a << 5;
    a >>>= 0;
    const result = (a + b) >>> 0;
    b = (b + 0x6d2b79f5) >>> 0;
    return result / 4294967296;
  };
}

/**
 * Everybody's chances when everybody's cards are known.
 *
 * A different question from `equity` above, and the difference matters. That
 * one asks "how does this hand do against opponents holding anything", which
 * is what a player weighing a call needs. This one asks "how does this hand do
 * against *those* hands", which is what a table shows once the cards are face
 * up and the only thing left is the board.
 *
 * Answering the second with the first would be wrong in both directions and
 * often wildly: ace-king against a known pair of twos is a coin flip, and
 * against two random cards it is a heavy favourite. Showing one number while
 * claiming the other is the kind of mistake nobody catches, because a
 * plausible percentage looks exactly like a correct one.
 *
 * Every hand gets a share of every pot it ties, so the shares sum to one.
 */
export function equityBetween(
  hands: readonly (readonly Card[])[],
  board: readonly Card[],
  samples: number,
  random: () => number,
): Equity[] {
  if (hands.length < 2) throw new Error('there has to be somebody to beat');
  if (board.length > 5) throw new Error('a board is at most five cards');
  for (const hand of hands) {
    if (hand.length !== 2) throw new Error('a hold-em hand is two cards');
  }

  const seen = new Set<Card>([...hands.flat(), ...board]);
  if (seen.size !== hands.length * 2 + board.length) throw new Error('a card appears twice');

  const unseen = orderedDeck().filter((card) => !seen.has(card));
  const needed = 5 - board.length;
  if (needed > unseen.length) throw new Error('not enough cards left to deal');

  const deck = unseen.slice();
  const runout = new Array<Card>(5);
  for (let i = 0; i < board.length; i += 1) runout[i] = board[i] as Card;
  const full = new Array<Card>(7);

  const wins = new Array<number>(hands.length).fill(0);
  const ties = new Array<number>(hands.length).fill(0);
  const pots = new Array<number>(hands.length).fill(0);

  // A board with nothing left to come is one deal, not a thousand of the same
  // one — and running it once keeps the answer exact rather than merely close.
  const rounds = needed === 0 ? 1 : samples;

  for (let sample = 0; sample < rounds; sample += 1) {
    for (let i = 0; i < needed; i += 1) {
      const j = i + Math.floor(random() * (deck.length - i));
      const picked = deck[j] as Card;
      deck[j] = deck[i] as Card;
      deck[i] = picked;
      runout[board.length + i] = picked;
    }

    let best = -1;
    let leaders = 0;
    const scores = new Array<number>(hands.length);
    for (let h = 0; h < hands.length; h += 1) {
      const hand = hands[h] as readonly Card[];
      full[0] = hand[0] as Card;
      full[1] = hand[1] as Card;
      for (let i = 0; i < 5; i += 1) full[2 + i] = runout[i] as Card;
      const score = evaluate(full, 7);
      scores[h] = score;
      if (score > best) {
        best = score;
        leaders = 1;
      } else if (score === best) {
        leaders += 1;
      }
    }

    for (let h = 0; h < hands.length; h += 1) {
      if (scores[h] !== best) continue;
      pots[h] = (pots[h] as number) + 1 / leaders;
      if (leaders === 1) wins[h] = (wins[h] as number) + 1;
      else ties[h] = (ties[h] as number) + 1;
    }
  }

  return hands.map(
    (_, h): Equity => ({
      share: (pots[h] as number) / rounds,
      win: (wins[h] as number) / rounds,
      tie: (ties[h] as number) / rounds,
      samples: rounds,
    }),
  );
}
